// Bot manager — spawns and updates every enemy/ally AI in the scene.
// Kept as a single component so we can iterate all bots efficiently per frame.
import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useMatchStore } from "@/stores/matchStore";
import { getEnemyDifficulty, getFriendlyDifficulty, SPAWN_PROTECTION_MS } from "@/game/systems/BotAI";
import { hasLOS, resolveCollisions } from "@/game/systems/collision";
import { Audio_ } from "@/lib/audio";

const BOT_RADIUS = 0.5;
const BOT_HEIGHT = 1.7;
const BOT_MAX_HP = 100;

/**
 * @param {object} props
 * @param {string} props.teamSize            e.g. "1v1", "5v5"
 * @param {string} props.enemyDifficulty     enemy difficulty key
 * @param {string} props.friendlyDifficulty  friendly difficulty key (defaults to enemyDifficulty)
 * @param {number} [props.enemyCount]        override number of enemies (defaults to teamSize)
 * @param {number} [props.friendlyCount]     override number of allies (defaults to teamSize-1)
 * @param {boolean} [props.friendlyFire]     allies can damage the player
 * @param {object} props.mapMeta             (walls, spawns, waypoints)
 * @param {React.MutableRefObject} props.playerRef
 */
export default function Bots({
    teamSize,
    enemyDifficulty,
    friendlyDifficulty,
    enemyCount,
    friendlyCount,
    friendlyFire = false,
    mapMeta,
    playerRef,
    onAllEnemiesDead,
    botsRef,
    // Legacy single-difficulty prop (used before per-team split)
    difficulty,
}) {
    // Backwards-compat: fall back to `difficulty` when the split props are omitted
    const enemyKey = enemyDifficulty || difficulty || "normal";
    const friendlyKey = friendlyDifficulty || difficulty || "normal";
    const enemyDiff = useMemo(() => getEnemyDifficulty(enemyKey), [enemyKey]);
    const friendlyDiff = useMemo(() => getFriendlyDifficulty(friendlyKey), [friendlyKey]);

    const teamSizeN = parseInt(String(teamSize).split("v")[0], 10) || 1;
    const numEnemies = Math.max(0, enemyCount ?? teamSizeN);
    const numAllies = Math.max(0, friendlyCount ?? teamSizeN - 1);

    // Bots data (mutable, non-reactive for perf)
    const bots = useMemo(() => {
        const arr = [];
        // Enemies
        for (let i = 0; i < numEnemies; i++) {
            const spawn = mapMeta.spawns.enemies[i % mapMeta.spawns.enemies.length];
            arr.push(makeBot({
                id: `E${i}`, name: `ENEMY-${i + 1}`, team: "enemy",
                pos: spawn, waypointIdx: (i * 3) % mapMeta.waypoints.length,
            }));
        }
        // Allies
        for (let i = 0; i < numAllies; i++) {
            const spawn = mapMeta.spawns.allies[i % mapMeta.spawns.allies.length];
            arr.push(makeBot({
                id: `A${i}`, name: `ALLY-${i + 1}`, team: "ally",
                pos: spawn, waypointIdx: (i * 5) % mapMeta.waypoints.length,
            }));
        }
        return arr;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamSize, mapMeta, numEnemies, numAllies]);

    // Expose bots list for weapon to raycast against
    useEffect(() => {
        if (botsRef) botsRef.current = bots;
    }, [bots, botsRef]);

    // Meshes refs by id (kept in ref map)
    const meshMap = useRef({});

    // Round reset: reset bot state whenever a new round starts
    const round = useMatchStore((s) => s.round);
    const phase = useMatchStore((s) => s.phase);
    useEffect(() => {
        const nowMs = performance.now();
        for (const b of bots) {
            b.hp = BOT_MAX_HP;
            b.alive = true;
            const spawn =
                b.team === "enemy"
                    ? mapMeta.spawns.enemies[b.idx % mapMeta.spawns.enemies.length]
                    : mapMeta.spawns.allies[b.idx % mapMeta.spawns.allies.length];
            b.position[0] = spawn[0];
            b.position[1] = spawn[1];
            b.position[2] = spawn[2];
            b.target = null;
            b.state = "patrol";
            b.stateUntil = 0;
            b.lastFireAt = 0;
            b.spawnedAt = nowMs;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [round, phase === "playing"]);

    // Player spawn protection — expose to updateBot via a ref so we don't rebuild the frame loop
    const playerSpawnAt = useRef(performance.now());
    useEffect(() => {
        playerSpawnAt.current = performance.now();
    }, [round, phase]);

    useFrame((_, dt) => {
        const match = useMatchStore.getState();
        if (match.phase !== "playing") return;
        dt = Math.min(dt, 0.05);
        const now = performance.now();

        const player = playerRef.current;
        if (!player) return;
        const playerPos = player.getPosition();

        for (const b of bots) {
            if (!b.alive) {
                // Fade out mesh
                const mesh = meshMap.current[b.id];
                if (mesh) mesh.visible = false;
                continue;
            }
            const mesh = meshMap.current[b.id];
            if (mesh) mesh.visible = true;

            updateBot(
                b,
                dt,
                now,
                b.team === "enemy" ? enemyDiff : friendlyDiff,
                mapMeta.walls,
                mapMeta.waypoints,
                playerPos,
                player,
                bots,
                { friendlyFire, playerSpawnAt: playerSpawnAt.current }
            );

            // Update mesh transform
            if (mesh) {
                mesh.position.set(b.position[0], b.position[1] + BOT_HEIGHT / 2, b.position[2]);
                if (b.facing !== undefined) mesh.rotation.y = b.facing;
            }
        }

        // Check win condition — all enemies dead
        const enemiesAlive = bots.filter((b) => b.team === "enemy" && b.alive).length;
        if (enemiesAlive === 0 && match.phase === "playing") {
            onAllEnemiesDead?.();
        }
    });

    return (
        <group>
            {bots.map((b) => (
                <BotMesh
                    key={b.id}
                    bot={b}
                    onMesh={(mesh) => (meshMap.current[b.id] = mesh)}
                />
            ))}
        </group>
    );
}

function makeBot({ id, name, team, pos, waypointIdx }) {
    const idx = parseInt(id.slice(1), 10);
    return {
        id, name, team, idx,
        position: [pos[0], pos[1], pos[2]],
        facing: 0,
        hp: BOT_MAX_HP,
        alive: true,
        state: "patrol", // patrol | engage | reposition | dead
        stateUntil: 0,
        target: null,
        waypointIdx: waypointIdx ?? 0,
        lastFireAt: 0,
        lastSeenAt: 0,
    };
}

function BotMesh({ bot, onMesh }) {
    const groupRef = useRef();
    useEffect(() => {
        if (groupRef.current) onMesh(groupRef.current);
    }, [onMesh]);

    const bodyColor = bot.team === "enemy" ? "#c62828" : "#1976d2";
    const trimColor = bot.team === "enemy" ? "#ff5252" : "#4fc3f7";

    return (
        <group ref={groupRef} position={[bot.position[0], bot.position[1] + BOT_HEIGHT / 2, bot.position[2]]}>
            {/* Body */}
            <mesh castShadow position={[0, 0, 0]}>
                <capsuleGeometry args={[BOT_RADIUS, 0.9, 4, 12]} />
                <meshStandardMaterial color={bodyColor} metalness={0.35} roughness={0.65} />
            </mesh>
            {/* Head */}
            <mesh castShadow position={[0, 0.85, 0]}>
                <sphereGeometry args={[0.28, 16, 16]} />
                <meshStandardMaterial color="#0f151b" metalness={0.6} roughness={0.4} />
            </mesh>
            {/* Visor glow */}
            <mesh position={[0, 0.85, 0.24]}>
                <boxGeometry args={[0.32, 0.08, 0.05]} />
                <meshBasicMaterial color={trimColor} />
            </mesh>
            {/* Shoulder trim */}
            <mesh position={[0, 0.35, 0]}>
                <torusGeometry args={[0.52, 0.03, 8, 24]} />
                <meshBasicMaterial color={trimColor} />
            </mesh>
        </group>
    );
}

function updateBot(b, dt, now, diff, walls, waypoints, playerPos, player, allBots, opts = {}) {
    const { friendlyFire = false, playerSpawnAt = 0 } = opts;

    // Spawn protection — this bot cannot fire until protection elapses
    if (now - (b.spawnedAt || 0) < SPAWN_PROTECTION_MS) {
        // Do the patrol shuffle only during protection so nothing feels frozen
        idlePatrol(b, dt, diff, waypoints, walls);
        return;
    }

    // Vision check
    const eye = new THREE.Vector3(b.position[0], b.position[1] + 1.5, b.position[2]);
    const playerEye = new THREE.Vector3(playerPos.x, playerPos.y + 1.4, playerPos.z);
    const distToPlayer = eye.distanceTo(playerEye);

    // Enemies acquire the player; friendlies acquire nearest enemy
    let target = null;
    let targetEye = null;
    let targetIsPlayer = false;
    let sawTarget = false;

    if (b.team === "enemy") {
        if (distToPlayer <= diff.engageRange) {
            if (hasLOS(eye, playerEye, walls, diff.engageRange)) {
                target = { position: [playerPos.x, playerPos.y, playerPos.z] };
                targetEye = playerEye;
                targetIsPlayer = true;
                sawTarget = true;
                b.lastSeenAt = now;
            }
        }
    } else {
        // Friendly — nearest visible enemy bot
        let best = null;
        let bestDist = Infinity;
        for (const other of allBots) {
            if (!other.alive || other.team !== "enemy") continue;
            const oe = new THREE.Vector3(other.position[0], other.position[1] + 1.5, other.position[2]);
            const d = eye.distanceTo(oe);
            if (d > diff.engageRange) continue;
            if (!hasLOS(eye, oe, walls, diff.engageRange)) continue;
            if (d < bestDist) {
                bestDist = d;
                best = { bot: other, eye: oe };
            }
        }
        if (best) {
            target = best.bot;
            targetEye = best.eye;
            sawTarget = true;
            b.lastSeenAt = now;
        }
    }

    // State transitions
    if (sawTarget) {
        if (b.state !== "engage") {
            b.state = "engage";
            b.stateUntil = now + diff.reactionMs;
        }
        b.currentTarget = target;
        b.currentTargetIsPlayer = targetIsPlayer;
    } else if (b.state === "engage" && now - b.lastSeenAt > 2500) {
        b.state = "patrol";
        b.currentTarget = null;
        b.currentTargetIsPlayer = false;
    }

    if (b.state === "engage" && target) {
        // Face target
        const dx = targetEye.x - eye.x;
        const dz = targetEye.z - eye.z;
        b.facing = Math.atan2(dx, dz);

        // Positioning: hold at desired distance, shuffle to peek
        const desiredDist = 8;
        const dToTarget = eye.distanceTo(targetEye);
        const dir = new THREE.Vector3(dx, 0, dz).normalize();
        const strafe = new THREE.Vector3(-dir.z, 0, dir.x).multiplyScalar(
            Math.sin(now / 500 + b.idx) * diff.peekChance
        );
        const moveDir = new THREE.Vector3();
        // Easy bots stop moving when about to fire
        const holdStill = diff.stopBeforeFire && now >= b.stateUntil - 200;
        if (!holdStill) {
            if (dToTarget > desiredDist + 2) moveDir.add(dir);
            else if (dToTarget < desiredDist - 2) moveDir.sub(dir);
            moveDir.add(strafe);
            if (moveDir.lengthSq() > 0.001) {
                moveDir.normalize().multiplyScalar(diff.moveSpeed * dt);
                b.position[0] += moveDir.x;
                b.position[2] += moveDir.z;
                const pos = new THREE.Vector3(b.position[0], b.position[1], b.position[2]);
                resolveCollisions(pos, BOT_RADIUS, walls);
                b.position[0] = pos.x;
                b.position[2] = pos.z;
            }
        }

        // Fire (after reaction time)
        if (now >= b.stateUntil && now - b.lastFireAt >= diff.fireIntervalMs) {
            b.lastFireAt = now;

            // Player spawn protection prevents damage during first ~SPAWN_PROTECTION_MS
            const playerProtected =
                targetIsPlayer && now - playerSpawnAt < SPAWN_PROTECTION_MS;

            // Roll accuracy — closer is easier, big wobble adds miss chance
            const distScaled = Math.min(dToTarget / diff.engageRange, 1);
            let hitChance = diff.accuracy * (1 - distScaled * 0.4);
            // Add wobble — big at low tiers, negligible at Impossible
            hitChance -= Math.random() * (diff.aimWobble || 0) * 0.4;
            hitChance = Math.max(0, Math.min(1, hitChance));

            if (!playerProtected && Math.random() <= hitChance) {
                const isHead = Math.random() < (diff.headshotChance || 0);
                let dmg = diff.damage * (1 + (Math.random() - 0.5) * diff.aimJitter * 0.4);
                if (isHead) dmg *= 1.8;

                if (targetIsPlayer) {
                    useMatchStore.getState().damagePlayer(dmg);
                } else {
                    // Ally attacks enemy — apply damage directly
                    const enemyBot = target;
                    enemyBot.hp -= dmg;
                    if (enemyBot.hp <= 0 && enemyBot.alive) {
                        enemyBot.alive = false;
                        enemyBot.state = "dead";
                        // Ally-kills don't count for player score, but they progress the round
                    }
                }
            }
            // Muted shot sound
            const s2 = 1 / Math.max(1, dToTarget * 0.5);
            Audio_.play("shoot", { volume: Math.min(0.22, s2) });
        }

        // Legacy friendly-fire: allies hitting the player only if enabled
        if (
            !targetIsPlayer &&
            friendlyFire &&
            b.team === "ally" &&
            distToPlayer < 3 &&
            Math.random() < 0.005
        ) {
            useMatchStore.getState().damagePlayer(1);
        }
    } else {
        idlePatrol(b, dt, diff, waypoints, walls);
    }
}

function idlePatrol(b, dt, diff, waypoints, walls) {
    const wp = waypoints[b.waypointIdx % waypoints.length];
    const dx = wp[0] - b.position[0];
    const dz = wp[2] - b.position[2];
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < 0.6) {
        b.waypointIdx = (b.waypointIdx + 1 + Math.floor(Math.random() * 2)) % waypoints.length;
        return;
    }
    const speed = diff.moveSpeed * 0.55 * dt;
    const nx = (dx / dist) * speed;
    const nz = (dz / dist) * speed;
    b.position[0] += nx;
    b.position[2] += nz;
    const pos = new THREE.Vector3(b.position[0], b.position[1], b.position[2]);
    resolveCollisions(pos, BOT_RADIUS, walls);
    b.position[0] = pos.x;
    b.position[2] = pos.z;
    b.facing = Math.atan2(dx, dz);
}

// Damage a bot from outside — called by Weapon on hit
export function applyDamageToBot(bots, id, dmg) {
    const b = bots.find((x) => x.id === id);
    if (!b || !b.alive) return;
    b.hp -= dmg;
    if (b.hp <= 0) {
        b.alive = false;
        b.state = "dead";
        useMatchStore.getState().onBotKilled(b.name);
    }
}
